import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, X, Send, Minimize2 } from 'lucide-react';
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  options?: string[];
}
interface ChatbotProps {
  isDark: boolean;
}
export function Chatbot({
  isDark
}: ChatbotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    text: 'Hi there! 👋 Welcome to Akios Business Management Suite. I can help you with:\n\n• Product information\n• Pricing and plans\n• Demo requests\n• Technical questions\n\nWhat would you like to know?',
    sender: 'bot',
    timestamp: new Date(),
    options: ['Tell me about features', 'Pricing plans', 'Request a demo', 'Integration options']
  }]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const getBotResponse = (userMessage: string): Message => {
    const lowerMessage = userMessage.toLowerCase();
    if (lowerMessage.includes('feature') || lowerMessage.includes('what') || lowerMessage.includes('capabilities')) {
      return {
        id: Date.now().toString(),
        text: 'Akios offers a complete business management suite including:\n\n✓ Inventory & Warehouse Management\n✓ Customer Relationship Management (CRM)\n✓ Financial Management & Accounting\n✓ Sales & Order Processing\n✓ Project Management\n✓ Business Intelligence & Analytics\n\nAll integrated in one platform! Would you like details on any specific module?',
        sender: 'bot',
        timestamp: new Date(),
        options: ['Inventory features', 'CRM capabilities', 'Financial tools', 'Request demo']
      };
    }
    if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('plan')) {
      return {
        id: Date.now().toString(),
        text: 'We offer flexible pricing plans:\n\n📦 Starter: $49/user/month\n🚀 Professional: $99/user/month\n🏢 Enterprise: Custom pricing\n\nAll plans include core features with 14-day free trial. Would you like to schedule a call to discuss which plan fits your needs?',
        sender: 'bot',
        timestamp: new Date(),
        options: ['Start free trial', 'Compare plans', 'Talk to sales', 'See features']
      };
    }
    if (lowerMessage.includes('demo') || lowerMessage.includes('trial')) {
      return {
        id: Date.now().toString(),
        text: 'Great! I can help you get started with a demo. You can:\n\n1. Start a 14-day free trial (no credit card required)\n2. Schedule a personalized demo with our team\n3. Watch a pre-recorded product tour\n\nWhich option works best for you?',
        sender: 'bot',
        timestamp: new Date(),
        options: ['Start free trial', 'Schedule demo', 'Watch video tour']
      };
    }
    if (lowerMessage.includes('integrat') || lowerMessage.includes('api') || lowerMessage.includes('connect')) {
      return {
        id: Date.now().toString(),
        text: 'Akios integrates seamlessly with 1000+ tools including:\n\n• Accounting: QuickBooks, Xero, Sage\n• E-commerce: Shopify, WooCommerce, Magento\n• Communication: Slack, Teams, Gmail\n• Payment: Stripe, PayPal, Square\n\nWe also provide REST APIs for custom integrations. Need help with a specific integration?',
        sender: 'bot',
        timestamp: new Date(),
        options: ['View all integrations', 'API documentation', 'Request custom integration']
      };
    }
    if (lowerMessage.includes('inventory')) {
      return {
        id: Date.now().toString(),
        text: 'Our Inventory Management includes:\n\n• Real-time stock tracking across multiple warehouses\n• Automated reorder points and purchase orders\n• Barcode scanning and serial number tracking\n• Batch and expiry date management\n• Stock movement history and audit trails\n\nPerfect for retail, manufacturing, and distribution businesses!',
        sender: 'bot',
        timestamp: new Date(),
        options: ['See demo', 'Pricing', 'More features']
      };
    }
    if (lowerMessage.includes('crm') || lowerMessage.includes('customer')) {
      return {
        id: Date.now().toString(),
        text: 'Our CRM module helps you:\n\n• Track all customer interactions in one place\n• Manage sales pipeline and opportunities\n• Automate follow-ups and reminders\n• Generate quotes and proposals\n• Analyze customer behavior and trends\n\nBuilt for sales teams that want to close more deals!',
        sender: 'bot',
        timestamp: new Date(),
        options: ['See CRM demo', 'Sales automation', 'Pricing']
      };
    }
    if (lowerMessage.includes('financial') || lowerMessage.includes('accounting')) {
      return {
        id: Date.now().toString(),
        text: 'Financial Management features:\n\n• Automated invoicing and payment tracking\n• Expense management and approval workflows\n• Multi-currency support\n• Tax calculation and compliance\n• Financial reports and P&L statements\n• Bank reconciliation\n\nStreamline your accounting processes!',
        sender: 'bot',
        timestamp: new Date(),
        options: ['View features', 'Accounting integrations', 'Request demo']
      };
    }
    return {
      id: Date.now().toString(),
      text: 'Thanks for your message! Our team specializes in helping businesses streamline operations with our all-in-one management suite. \n\nWould you like to:\n• Learn about specific features\n• See pricing plans\n• Schedule a demo\n• Speak with our sales team',
      sender: 'bot',
      timestamp: new Date(),
      options: ['Features', 'Pricing', 'Demo', 'Contact sales']
    };
  };
  const handleSend = (message?: string) => {
    const textToSend = message || inputValue;
    if (!textToSend.trim()) return;
    const newUserMessage: Message = {
      id: Date.now().toString(),
      text: textToSend,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setInputValue('');
    setTimeout(() => {
      const botResponse = getBotResponse(textToSend);
      setMessages(prev => [...prev, botResponse]);
    }, 800);
  };
  const handleOptionClick = (option: string) => {
    handleSend(option);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };
  return <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end" data-id="element-35">
      <div className={`mb-4 w-[350px] md:w-[400px] rounded-2xl overflow-hidden transition-all duration-300 origin-bottom-right shadow-2xl ${isDark ? 'bg-gray-800/95 border border-gray-700' : 'glass-panel'} ${isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-10 pointer-events-none absolute bottom-0 right-0'}`} style={{
      height: isOpen ? '550px' : '0px'
    }} data-id="element-36">
        <div className={`backdrop-blur-md p-4 border-b flex justify-between items-center ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-white/50 border-gray-100'}`} data-id="element-37">
          <div className="flex items-center gap-3" data-id="element-38">
            <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center" data-id="element-39">
              <MessageSquare className="w-4 h-4 text-white" data-id="element-40" />
            </div>
            <div data-id="element-41">
              <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-42">
                Akios Assistant
              </h3>
              <p className="text-xs text-brand-600 flex items-center gap-1" data-id="element-43">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" data-id="element-44"></span>
                Online
              </p>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-45">
            <Minimize2 className="w-4 h-4" data-id="element-46" />
          </button>
        </div>

        <div className={`flex-1 overflow-y-auto p-4 space-y-4 h-[400px] ${isDark ? 'bg-gray-900/30' : 'bg-white/30'}`} data-id="element-47">
          {messages.map(msg => <div key={msg.id} data-id="element-48">
              <div className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-49">
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed shadow-sm whitespace-pre-line ${msg.sender === 'user' ? 'bg-brand-500 text-white rounded-tr-none' : isDark ? 'bg-gray-800 text-gray-200 rounded-tl-none border border-gray-700' : 'bg-white text-gray-700 rounded-tl-none border border-gray-100'}`} data-id="element-50">
                  {msg.text}
                </div>
              </div>

              {msg.options && msg.sender === 'bot' && <div className="flex flex-wrap gap-2 mt-3 ml-2" data-id="element-51">
                  {msg.options.map((option, idx) => <button key={idx} onClick={() => handleOptionClick(option)} className={`px-3 py-1.5 border rounded-full text-xs font-medium transition-colors ${isDark ? 'bg-gray-800 border-brand-700 text-brand-400 hover:bg-gray-700' : 'bg-white border-brand-200 text-brand-700 hover:bg-brand-50'}`} data-id="element-52">
                      {option}
                    </button>)}
                </div>}
            </div>)}
          <div ref={messagesEndRef} data-id="element-53" />
        </div>

        <form onSubmit={handleSubmit} className={`p-3 border-t ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-white/50 border-gray-100'}`} data-id="element-54">
          <div className="relative flex items-center gap-2" data-id="element-55">
            <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Type a message..." className={`w-full pl-4 pr-10 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`} data-id="element-56" />
            <button type="submit" disabled={!inputValue.trim()} className="absolute right-2 p-1.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-57">
              <Send className="w-3.5 h-3.5" data-id="element-58" />
            </button>
          </div>
        </form>
      </div>

      <button onClick={() => setIsOpen(!isOpen)} className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-105 ${isOpen ? 'bg-gray-900 rotate-90' : 'bg-brand-500 hover:bg-brand-600'}`} data-id="element-59">
        {isOpen ? <X className="w-6 h-6 text-white" data-id="element-60" /> : <MessageSquare className="w-6 h-6 text-white" data-id="element-61" />}
      </button>
    </div>;
}